/**
 * src/shell/portal/components/ChannelCreationModal.tsx
 *
 * Consumer channel creation modal.
 * Configures slug, title, consumer category, access spectrum, and minimum score threshold.
 */

import React, { useState } from 'react';
import {
  ChannelCategory,
  ChannelAccessLevel,
  SocialChannel,
} from '../../../domain/social/channel-contracts';
import { channelManager } from '../../../domain/social/channel-manager';
import { X, Globe, Lock, Volume2, Shield } from 'lucide-react';

export interface ChannelCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChannelCreated: (channel: SocialChannel) => void;
  creatorPersonaId: string;
  creatorDisplayName: string;
  creatorHandle: string;
}

export const ChannelCreationModal: React.FC<ChannelCreationModalProps> = ({
  isOpen,
  onClose,
  onChannelCreated,
  creatorPersonaId,
  creatorDisplayName,
  creatorHandle,
}) => {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ChannelCategory>('creators-media');
  const [accessLevel, setAccessLevel] = useState<ChannelAccessLevel>('public');
  const [minScore, setMinScore] = useState<number>(20);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!slug || slug === title.toLowerCase().replace(/[^a-z0-9]/g, '-')) {
      setSlug(val.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 24));
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (!title.trim()) throw new Error('Channel title is required.');
      if (!slug.trim()) throw new Error('Channel handle/slug is required.');

      const newChan = channelManager.createChannel({
        title: title.trim(),
        slug: slug.trim(),
        description: description.trim() || 'A new community channel on Herx.',
        category,
        accessLevel,
        ownerPersonaId: creatorPersonaId,
        ownerDisplayName: creatorDisplayName,
        ownerHandle: creatorHandle,
        minSovereignScore: minScore,
        tags: [category],
      });

      onChannelCreated(newChan);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create channel.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#0c101a',
          border: '1px solid #1e293b',
          borderRadius: 16,
          width: '100%',
          maxWidth: 480,
          color: '#f8fafc',
          boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16 }}>Create New Social Channel</div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleCreate} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', padding: '8px 12px', borderRadius: 8, color: '#fca5a5', fontSize: 12 }}>
              {error}
            </div>
          )}

          {/* Title & Slug */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
              Channel Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="e.g. KL Street Food & Nightlife"
              style={{
                width: '100%',
                background: '#070a10',
                border: '1px solid #1e293b',
                borderRadius: 8,
                padding: '10px 12px',
                color: '#ffffff',
                fontSize: 14,
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
              Handle / Slug
            </label>
            <div style={{ display: 'flex', alignItems: 'center', background: '#070a10', border: '1px solid #1e293b', borderRadius: 8, padding: '0 12px' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>herx.org/#/c/</span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="kl-street-food"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  padding: '10px 4px',
                  color: '#38bdf8',
                  fontSize: 14,
                  fontWeight: 600,
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
              Community Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ChannelCategory)}
              style={{
                width: '100%',
                background: '#070a10',
                border: '1px solid #1e293b',
                borderRadius: 8,
                padding: '10px 12px',
                color: '#ffffff',
                fontSize: 13,
                outline: 'none',
              }}
            >
              <option value="town-square">🏛️ Town Square (Open Dialogue)</option>
              <option value="creators-media">🎨 Creators & Media (Showcases & Art)</option>
              <option value="tech-depin">⚡ Tech & DePIN (Nodes & Hardware)</option>
              <option value="local-hangouts">📍 Local Hangouts (Geo-Scoped)</option>
              <option value="private-circles">🔒 Private Circles (Invite-Only)</option>
            </select>
          </div>

          {/* Access Level Spectrum */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
              Access Level Spectrum
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div
                onClick={() => setAccessLevel('public')}
                style={{
                  background: accessLevel === 'public' ? 'rgba(56, 189, 248, 0.15)' : '#070a10',
                  border: accessLevel === 'public' ? '1px solid #38bdf8' : '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 10,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <Globe size={16} color={accessLevel === 'public' ? '#38bdf8' : '#64748b'} style={{ margin: '0 auto 4px auto' }} />
                <div style={{ fontSize: 11, fontWeight: 700, color: accessLevel === 'public' ? '#f8fafc' : '#94a3b8' }}>Public</div>
                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Open discovery</div>
              </div>

              <div
                onClick={() => setAccessLevel('community-read-only')}
                style={{
                  background: accessLevel === 'community-read-only' ? 'rgba(245, 158, 11, 0.15)' : '#070a10',
                  border: accessLevel === 'community-read-only' ? '1px solid #f59e0b' : '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 10,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <Volume2 size={16} color={accessLevel === 'community-read-only' ? '#f59e0b' : '#64748b'} style={{ margin: '0 auto 4px auto' }} />
                <div style={{ fontSize: 11, fontWeight: 700, color: accessLevel === 'community-read-only' ? '#f8fafc' : '#94a3b8' }}>Broadcast</div>
                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Read-only feed</div>
              </div>

              <div
                onClick={() => setAccessLevel('invite-only')}
                style={{
                  background: accessLevel === 'invite-only' ? 'rgba(168, 85, 247, 0.15)' : '#070a10',
                  border: accessLevel === 'invite-only' ? '1px solid #a855f7' : '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 10,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <Lock size={16} color={accessLevel === 'invite-only' ? '#a855f7' : '#64748b'} style={{ margin: '0 auto 4px auto' }} />
                <div style={{ fontSize: 11, fontWeight: 700, color: accessLevel === 'invite-only' ? '#f8fafc' : '#94a3b8' }}>Invite-Only</div>
                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Encrypted circle</div>
              </div>
            </div>
          </div>

          {/* Threshold Sovereign Score */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
                Minimum Sovereign Score to Post
              </label>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8' }}>{minScore} pts</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="10"
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#0284c7' }}
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              Filters out newly created accounts and spam bots.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                background: '#1e293b',
                border: 'none',
                borderRadius: 8,
                padding: '10px 14px',
                color: '#94a3b8',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                flex: 2,
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                border: 'none',
                borderRadius: 8,
                padding: '10px 14px',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Create Channel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
